import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { WebsiteAuditService } from '../../website-audit.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ConfirmationComponent } from 'src/app/modals/confirmation/confirmation.component';

interface CompetitorRow {
  competitorId?: string;
  competitorName?: string;
  name?: string;
  domain?: string;
  url?: string;
  isActive: boolean;
  updating?: boolean;
  // Backend crawl signals: a competitor is only ready once `isUsable` is true;
  // latest/selectedStatus surface a hard failure ("Failed").
  isUsable?: boolean;
  latestStatus?: string;
  selectedStatus?: string;
}

@Component({
  selector: 'app-add-competitor',
  templateUrl: './add-competitor.component.html',
  styleUrls: ['../add-task/add-task.component.css', './add-competitor.component.css'],
})
export class AddCompetitorComponent implements OnInit, OnDestroy {
  competitorName = '';
  competitorUrl = '';
  isAdding = false;

  // Existing competitors shown above the add form, each with an on/off toggle.
  competitors: CompetitorRow[] = [];
  // Re-pulling the task for fresh isActive flags.
  loadingCompetitors = false;
  // True once anything changed (added or toggled) so the caller refetches.
  private changed = false;
  // While the modal is open we poll the task so a just-added competitor flips
  // from "Loading" to ready on its own. `loadingIds` are the competitors we've
  // seen still crawling, so we can auto-activate exactly the ones that finish
  // (never a competitor the user deliberately turned off).
  private pollTimer: any = null;
  private destroyed = false;
  private loadingIds = new Set<string>();
  // Pending page reload after a just-added competitor becomes usable (see
  // reloadForReadyCompetitor). Held so it can be cancelled if the modal closes.
  private reloadTimer: any = null;

  constructor(
    public dialogRef: MatDialogRef<AddCompetitorComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      taskId?: string;
      competitors?: any[];
      activeCompetitorIds?: string[];
    },
    private websiteAuditService: WebsiteAuditService,
    private notificationService: NotificationService,
    private dialog: MatDialog,
  ) {
    // Seed from the in-memory snapshot for an instant render…
    this.competitors = this.buildRows(
      this.data?.competitors || [],
      this.data?.activeCompetitorIds,
    );
  }

  ngOnInit(): void {
    // Re-pull the task so isActive reflects the current backend state
    // (the /competitors endpoint doesn't return the flag; the task does).
    this.refreshCompetitors();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.reloadTimer) {
      clearTimeout(this.reloadTimer);
      this.reloadTimer = null;
    }
  }

  // A just-added competitor became usable (immediately, or after its crawl
  // finished mid-poll). The benchmark views ("benchmarked against …", the score
  // bars) are derived data a task update alone won't rebuild, so reload the page
  // once to pull everything fresh. Guarded so overlapping polls schedule it once.
  private reloadForReadyCompetitor(): void {
    if (this.reloadTimer || this.destroyed) {
      return;
    }
    // Brief delay so the "ready" toast is visible before the reload.
    this.reloadTimer = setTimeout(() => window.location.reload(), 1200);
  }

  // `silent` keeps the "refreshing…" hint hidden — used by the background poll.
  private refreshCompetitors(silent = false): void {
    const taskId = this.data?.taskId;
    if (!taskId) {
      return;
    }
    if (!silent) {
      this.loadingCompetitors = true;
    }
    this.websiteAuditService.getAuditTask(taskId).subscribe({
      next: (res: any) => {
        this.loadingCompetitors = false;
        const list = res?.availableCompetitors;
        if (Array.isArray(list)) {
          const rows = this.buildRows(list, this.data?.activeCompetitorIds);
          const ids = new Set(rows.map((r) => r.competitorId).filter(Boolean));
          // Keep any just-added row the backend list hasn't echoed back yet, so
          // it doesn't flicker out of the list between add and the next poll.
          const pending = this.competitors.filter(
            (c) =>
              c.competitorId &&
              this.loadingIds.has(c.competitorId) &&
              !ids.has(c.competitorId),
          );
          this.competitors = [...rows, ...pending];
          this.reconcileLoading();
        }
        this.schedulePoll();
      },
      error: () => {
        this.loadingCompetitors = false;
        // Keep polling while something is still loading — a transient error
        // shouldn't strand a competitor on "Loading".
        this.schedulePoll();
      },
    });
  }

  // ─── Crawl state ────────────────────────────────────────────────────────────
  // A competitor is only ready once the backend reports `isUsable`. Until then
  // it's still crawling ("Loading"); a hard failure surfaces via latestStatus.
  compState(c: CompetitorRow): 'loading' | 'failed' | 'ready' {
    const status = String(
      c?.latestStatus || c?.selectedStatus || '',
    ).toLowerCase();
    if (status === 'failed' || status === 'error') {
      return 'failed';
    }
    if (c?.isUsable === false) {
      return 'loading';
    }
    return 'ready';
  }

  isLoadingRow(c: CompetitorRow): boolean {
    return this.compState(c) === 'loading';
  }

  isFailedRow(c: CompetitorRow): boolean {
    return this.compState(c) === 'failed';
  }

  // Any competitor still crawling → keep polling for its result.
  private hasLoading(): boolean {
    return this.competitors.some((c) => this.compState(c) === 'loading');
  }

  private schedulePoll(): void {
    if (this.destroyed || this.pollTimer || !this.hasLoading()) {
      return;
    }
    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;
      this.refreshCompetitors(true);
    }, 6000);
  }

  // Auto-activate the competitors that finish their initial crawl, and surface a
  // toast for any that fail. Only touches competitors we actually saw loading.
  private reconcileLoading(): void {
    this.competitors.forEach((c) => {
      const id = c.competitorId;
      if (!id) {
        return;
      }
      const state = this.compState(c);
      if (state === 'loading') {
        this.loadingIds.add(id);
      } else if (state === 'ready' && this.loadingIds.has(id)) {
        this.loadingIds.delete(id);
        this.changed = true;
        // The backend usually flips isActive itself on completion; only nudge it
        // if it came back ready-but-inactive so the competitor is usable.
        if (!c.isActive) {
          this.autoActivate(c);
        } else {
          this.notificationService.info(`“${this.getRowLabel(c)}” is ready.`);
          this.reloadForReadyCompetitor();
        }
      } else if (state === 'failed' && this.loadingIds.has(id)) {
        this.loadingIds.delete(id);
        this.notificationService.error(
          `“${this.getRowLabel(c)}” could not be crawled.`,
        );
      }
    });
  }

  private autoActivate(row: CompetitorRow): void {
    const taskId = this.data?.taskId;
    if (!taskId || !row.competitorId) {
      return;
    }
    row.updating = true;
    this.websiteAuditService
      .setCompetitorActive(taskId, row.competitorId, true)
      .subscribe({
        next: () => {
          row.updating = false;
          row.isActive = true;
          this.changed = true;
          this.notificationService.info(`“${this.getRowLabel(row)}” is ready.`);
          this.reloadForReadyCompetitor();
        },
        error: () => {
          row.updating = false;
        },
      });
  }

  // availableCompetitors carries the real `isActive` flag per competitor, so
  // use it directly. Fall back to the benchmark-set ids (or true) only for
  // older payloads that don't include the flag.
  private buildRows(list: any[], activeIds?: string[]): CompetitorRow[] {
    const hasIds = Array.isArray(activeIds) && activeIds.length > 0;
    const activeSet = new Set((activeIds || []).filter(Boolean));
    return (list || []).map((c: any) => ({
      ...c,
      isActive:
        c?.isActive !== undefined
          ? !!c.isActive
          : hasIds
            ? activeSet.has(c?.competitorId)
            : true,
    }));
  }

  getRowLabel(c: CompetitorRow): string {
    return (
      c?.competitorName ||
      c?.name ||
      c?.domain ||
      this.getDomain(c?.url || '') ||
      'Competitor'
    );
  }

  getRowUrl(c: CompetitorRow): string {
    return c?.domain || this.getDomain(c?.url || '');
  }

  getDomain(url: string): string {
    if (!url) return '';
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;
      return new URL(normalized).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }

  // Toggling on/off starts or stops auditing this competitor, so confirm first.
  onToggle(row: CompetitorRow, event: any) {
    const desired = !!event?.checked;
    const previous = row.isActive;
    const taskId = this.data?.taskId;

    const revert = () => {
      row.isActive = previous;
      if (event?.source) event.source.checked = previous;
    };

    if (!taskId || !row?.competitorId) {
      revert();
      this.notificationService.error('Missing competitor id.');
      return;
    }

    // Keep the toggle visually in the desired position while confirming.
    row.isActive = desired;
    const name = this.getRowLabel(row);

    const ref = this.dialog.open(ConfirmationComponent, {
      width: '430px',
      data: {
        header: desired ? 'Resume audit' : 'Stop audit',
        content: desired
          ? `This will resume the audit of the competitor “${name}”. Continue?`
          : `This will stop the audit of the competitor “${name}”. Continue?`,
        confirm_btn: desired ? 'resume' : 'stop',
        confirmButtonDangerStyle: !desired,
      },
    });

    this.subscribeConfirm(ref, row, taskId, desired, revert);
  }

  private subscribeConfirm(
    ref: MatDialogRef<ConfirmationComponent>,
    row: CompetitorRow,
    taskId: string,
    desired: boolean,
    revert: () => void,
  ) {
    ref.afterClosed().subscribe((result: any) => {
      if (result != 1) {
        revert();
        return;
      }

      row.updating = true;
      this.websiteAuditService
        .setCompetitorActive(taskId, row.competitorId as string, desired)
        .subscribe({
          next: () => {
            row.updating = false;
            this.changed = true;
            this.notificationService.info(
              desired
                ? `Audit resumed for “${this.getRowLabel(row)}”.`
                : `Audit stopped for “${this.getRowLabel(row)}”.`,
            );
          },
          error: (err: any) => {
            row.updating = false;
            revert();
            this.notificationService.error(
              err?.error?.message || 'Could not update competitor status.',
            );
          },
        });
    });
  }

  add() {
    const taskId = this.data?.taskId;
    const rawUrl = (this.competitorUrl || '').trim();
    const name = (this.competitorName || '').trim();

    if (!taskId) {
      this.notificationService.error('Missing task.');
      return;
    }
    if (!rawUrl) {
      this.notificationService.info('Competitor URL is required.');
      return;
    }
    // If the user omitted the scheme, default to https:// rather than erroring.
    const url = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;

    this.isAdding = true;
    this.websiteAuditService
      .addCompetitor(taskId, { competitorName: name, competitorUrl: url })
      .subscribe({
        next: (res: any) => {
          this.isAdding = false;
          const payload = res?.data ?? res;
          // Some backends signal a soft failure via a status field on a 200.
          if (payload?.status && payload.status !== 200 && payload.status !== 201) {
            this.notificationService.error(
              payload?.message || 'Could not add competitor.',
            );
            return;
          }
          // Show the new competitor immediately as "Loading" (isUsable=false)
          // until its first crawl finishes. Merge over any existing row with the
          // same id so we don't duplicate it. Fall back to the entered values
          // when the response doesn't echo the created competitor.
          const created = payload?.competitor ?? payload ?? {};
          const createdId = created?.competitorId;
          const newRow: CompetitorRow = {
            ...created,
            competitorId: createdId,
            competitorName: created?.competitorName || created?.name || name,
            url: created?.url || url,
            domain: created?.domain,
            // Freshly added → still crawling. Only treat it as usable if the
            // backend already said so.
            isUsable: created?.isUsable === true,
            latestStatus: created?.latestStatus,
            selectedStatus: created?.selectedStatus,
            isActive: created?.isActive === true,
          };
          this.competitors = [
            ...this.competitors.filter(
              (c) => !createdId || c.competitorId !== createdId,
            ),
            newRow,
          ];
          const readyInstantly = this.compState(newRow) === 'ready';
          if (createdId && this.compState(newRow) === 'loading') {
            this.loadingIds.add(createdId);
          }
          this.competitorName = '';
          this.competitorUrl = '';
          this.changed = true;
          this.notificationService.info(
            readyInstantly
              ? `Competitor added and ready!`
              : `Competitor added! It may take a few minutes to finish loading. ` +
                  `We'll enable it automatically once it's ready.`,
          );
          // Usable right away → reload so the benchmark views pick it up (same
          // as when a crawl finishes mid-poll). Otherwise pull the authoritative
          // state and poll until the crawl finishes.
          if (readyInstantly) {
            this.reloadForReadyCompetitor();
            return;
          }
          this.refreshCompetitors(true);
        },
        error: (err: any) => {
          this.isAdding = false;
          const status = err?.status;
          const msg =
            err?.error?.message ||
            err?.error?.error ||
            (status === 409 ? 'This competitor is already added.' : '') ||
            (status === 400 ? 'That URL doesn’t look valid.' : '') ||
            (status === 0
              ? 'Network error — please check your connection and try again.'
              : '') ||
            'Could not add competitor.';
          this.notificationService.error(msg);
        },
      });
  }

  isDisabled() {
    return (
      this.competitorName.trim().length === 0 ||
      this.competitorUrl.trim().length === 0 ||
      this.isAdding
    );
  }

  close() {
    this.dialogRef.close(this.changed ? 1 : 0);
  }
}
